/** @jsx jsx */
import { jsx } from '@emotion/core'
import { ProcedimentoOdontoSelectModel } from 'components/form/field/select/ProcedimentoOdontoSelectField'
import { resolveName, UseFieldProps } from 'components/form/final-form/useField'
import { ParteBucalEnum } from 'graphql/types.generated'
import { Fragment, useState } from 'react'
import { Field, FieldRenderProps } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { Arcadas } from '../../odontograma/Arcadas'
import { convertArcadasFieldModelToArcadasModel } from '../converter'
import { OdontogramaFieldModel } from '../OdontogramaField'
import ArcadaFormPopper, { ArcadaFormModel } from './ArcadaFormPopper'

export interface ArcadaFieldModel {
  observacoes?: string[]
  procedimentos?: ProcedimentoOdontoSelectModel[]
}

export interface ArcadasFieldProps extends UseFieldProps {
  name: MetaPath<Record<ParteBucalEnum, ArcadaFieldModel>>
  cidadaoDataNascimento: LocalDate
  proteseTotalSuperior?: boolean
  proteseTotalInferior?: boolean
  initialValue?: OdontogramaFieldModel['arcadas']
}

interface ArcadaActive {
  parteBucalEnum: ParteBucalEnum
  ref: HTMLButtonElement
}

export default function ArcadasField(props: ArcadasFieldProps) {
  const { name, proteseTotalSuperior, proteseTotalInferior, cidadaoDataNascimento, initialValue } = props
  const [popperOpen, setPopperOpen] = useState(false)
  const [activeArcada, setActiveArcada] = useState<ArcadaActive>()

  const onClick = (arcada: ParteBucalEnum, ref: HTMLButtonElement) => {
    setActiveArcada({ parteBucalEnum: arcada, ref })
    setPopperOpen(true)
  }

  const renderField = (fieldProps: FieldRenderProps<OdontogramaFieldModel['arcadas'], any>) => {
    const {
      input: { value, onChange },
    } = fieldProps

    const fieldValues = Object.entries(value) as [ParteBucalEnum, ArcadaFieldModel][]

    const handleChange = (newValues: [ParteBucalEnum, ArcadaFieldModel][]) => {
      const o = Object.fromEntries(new Map([...fieldValues, ...newValues] as [ParteBucalEnum, ArcadaFieldModel][]))
      onChange(o)
    }

    const handleSubmit = (formValues: ArcadaFormModel) => {
      const { replicarItems, ...values } = formValues

      setPopperOpen(false)

      values &&
        handleChange([
          ...((replicarItems &&
            replicarItems.map((replicarItem) => [replicarItem.value, values] as [ParteBucalEnum, ArcadaFieldModel])) ||
            []),
          [activeArcada.parteBucalEnum, values],
        ])

      setActiveArcada(undefined)
    }

    return (
      <Fragment>
        <Arcadas
          onClick={onClick}
          activeItem={activeArcada?.parteBucalEnum}
          proteseTotalSuperior={proteseTotalSuperior}
          proteseTotalInferior={proteseTotalInferior}
          value={convertArcadasFieldModelToArcadasModel(value)}
        />

        {activeArcada && (
          <ArcadaFormPopper
            parteBucalEnum={activeArcada?.parteBucalEnum}
            cidadaoDataNascimento={cidadaoDataNascimento}
            open={popperOpen}
            placement='top'
            anchorRef={activeArcada.ref}
            initialValues={value[activeArcada.parteBucalEnum]}
            arcadasValues={value}
            onSubmit={handleSubmit}
          />
        )}
      </Fragment>
    )
  }

  return (
    <Field name={resolveName(name)} render={renderField} initialValue={initialValue} subscription={{ value: true }} />
  )
}
